import { useState, useRef, useEffect } from 'react';
import { ChevronDown, UserCog, Moon, Sun, LogOut, User as UserIcon } from 'lucide-react';
import { useThemeStore } from '@/store/themeStore';
import { cn } from '@/lib/utils/cn';
import { env } from '@/lib/config/env';

interface AccountMenuProps {
  user: { fullName?: string; role?: string } | null;
  onAccount: () => void;
  onLogout: () => void;

  showAccount?: boolean;

  singleInitial?: boolean;
}

export function AccountMenu({
  user,
  onAccount,
  onLogout,
  showAccount = true,
  singleInitial = false,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const resolved = useThemeStore((s) => s.resolved);
  const toggle = useThemeStore((s) => s.toggle);

  const initials = user?.fullName
    ?.split(' ')
    .map((p) => p[0])
    .slice(0, singleInitial ? 1 : 2)
    .join('')
    .toUpperCase();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-2 rounded-lg px-2 py-1.5 transition',
          open ? 'bg-surface-muted' : 'hover:bg-surface-muted',
        )}
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-bold text-white shadow-sm">
          {initials ?? <UserIcon className="h-4 w-4" />}
        </div>
        <div className="hidden text-left leading-tight md:block">
          <p className="text-[9px] capitalize tracking-wider text-content-subtle">
            {user?.role?.replace('_', ' ')}
          </p>
          <p className="text-xs font-semibold text-content">{user?.fullName}</p>
        </div>
        <ChevronDown
          className={cn(
            'hidden h-3.5 w-3.5 text-content-subtle transition-transform md:block',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-border bg-surface-elevated p-1.5 shadow-lg animate-fade-in">
          <div className="border-b border-border px-3 py-2 mb-1">
            <p className="text-sm font-semibold text-content">{user?.fullName}</p>
            <p className="text-xs capitalize text-content-muted">{user?.role?.replace('_', ' ')}</p>
          </div>
          {showAccount && (
            <button
              type="button"
              onClick={() => {
                onAccount();
                setOpen(false);
              }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-content transition hover:bg-surface-muted"
            >
              <UserCog className="h-4 w-4 text-content-muted" />
              Account
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              toggle();
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-content transition hover:bg-surface-muted"
          >
            {resolved === 'dark' ? (
              <Sun className="h-4 w-4 text-content-muted" />
            ) : (
              <Moon className="h-4 w-4 text-content-muted" />
            )}
            {resolved === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
          <div className="mt-1 border-t border-border px-3 pb-1 pt-2 text-center text-[10px] text-content-subtle">
            {env.appName} · v{env.appVersion}
          </div>
        </div>
      )}
    </div>
  );
}
